import { type ParentWindow, getParentWindow, openPopup } from "../util/crossWindow"
import type { Ports } from "./windowElm"
import { type Config } from "@ironzion/frontend-js-config"

const PlatformAuthEventType = {
  CodeAuthInitiated: "CodeAuthInitiated",
  CodeAuthInProgress: "CodeAuthInProgress",
  CodeAuthFailed: "CodeAuthFailed",

  GoogleAuthInitiated: "GoogleAuthInitiated",
  GoogleAuthInProgress: "GoogleAuthInProgress",
  GoogleAuthFailed: "GoogleAuthFailed",

  SamlAuthInitiated: "SamlAuthInitiated",
  SamlAuthInProgress: "SamlAuthInProgress",
  SamlAuthFailed: "SamlAuthFailed",

  RedirectIframe: "RedirectIframe",

  AuthSucceeded: "AuthSucceeded",

  OAuthCancelled: "OAuthCancelled",
} as const

const PlatformConnectEventType = {
  Connect: "Connect",
} as const

const MarketplaceAuthEventType = {
  AuthFailed: "AuthFailed",
} as const

const PlatformAuthFailedReason = {
  AuthError: "AuthError",
  Cancelled: "Cancelled",
  Timeout: "Timeout",
  PopupsBlocked: "PopupsBlocked",
} as const

type PlatformAuthFailedReason = typeof PlatformAuthFailedReason[keyof typeof PlatformAuthFailedReason]

export type PlatformAuthEvent =
  /* Code Authentication */
  | { tag: typeof PlatformAuthEventType.CodeAuthInitiated; email: string }
  | { tag: typeof PlatformAuthEventType.CodeAuthInProgress; code: string }
  | { tag: typeof PlatformAuthEventType.CodeAuthFailed; reason: PlatformAuthFailedReason }
  /* Google Authentication */
  | { tag: typeof PlatformAuthEventType.GoogleAuthInitiated }
  | { tag: typeof PlatformAuthEventType.GoogleAuthInProgress; code: string }
  | { tag: typeof PlatformAuthEventType.GoogleAuthFailed; reason: PlatformAuthFailedReason }
  /* Saml Authentication */
  | { tag: typeof PlatformAuthEventType.SamlAuthInitiated }
  | { tag: typeof PlatformAuthEventType.SamlAuthInProgress }
  | { tag: typeof PlatformAuthEventType.SamlAuthFailed; reason: PlatformAuthFailedReason }
  | { tag: typeof PlatformAuthEventType.AuthSucceeded }
  | { tag: typeof PlatformAuthEventType.RedirectIframe; redirectTo: string }
  | { tag: typeof PlatformAuthEventType.OAuthCancelled }

export type PlatformConnectEvent = { tag: typeof PlatformConnectEventType.Connect; nonce: string }

export type MarketplaceAuthEvent = { tag: typeof MarketplaceAuthEventType.AuthFailed; reason: string }

type ElmPort = Pick<Ports, "fromWorld">

export interface Marketplace {
  sendEvent: (event: PlatformAuthEvent) => void
}

export class MarketplaceChannel extends EventTarget implements Marketplace {
  private readonly parent: ParentWindow
  private readonly config: Config
  private readonly elm: ElmPort
  private readonly marketplaceReceiver: MessagePort
  private readonly platformReceiver: MessagePort

  private constructor(
    parent: ParentWindow,
    config: Config,
    elm: ElmPort,
    marketplaceReceiver: MessagePort,
    platformReceiver: MessagePort,
  ) {
    super()
    this.parent = parent
    this.config = config
    this.elm = elm
    this.marketplaceReceiver = marketplaceReceiver
    this.platformReceiver = platformReceiver
  }

  /**
   * This will send a message to the parent window, transferring over the port that can
   * be used to send messages back to Platform. The `targetOrigin` ensures that it can
   * only be received by the marketplace.
   */
  private connect(event: PlatformConnectEvent) {
    this.parent.window.postMessage(event, {
      targetOrigin: this.config.marketplaceUrl,
      transfer: [this.platformReceiver],
    })
  }

  /**
   * Send a message to the marketplace.
   *
   * Private method to use for possible future updates without
   * affecting public api `sendEvent`
   */
  private send(event: PlatformAuthEvent) {
    this.marketplaceReceiver.postMessage(event)
  }

  /**
   * Send a message to the marketplace
   */
  sendEvent(event: PlatformAuthEvent) {
    this.send(event)
  }

  /**
   * Given the `Config` object for the Platform app,
   * attempt to connect to the Marketplace in the case
   * that it's loading the Platform app to authenticate
   * a user
   */
  static async connect(config: Config, elm: ElmPort) {
    const parentWindow = getParentWindow()

    if (!parentWindow) {
      return null
    }

    /**
     * https://developer.mozilla.org/en-US/docs/Web/API/Channel_Messaging_API/Using_channel_messaging
     *
     * Allows transferring an object to a different window/tab/frame/iframe which
     * can communicate back and forth between this browsing context and the other one
     */
    const rawChannel = new MessageChannel()

    /**
     * This port can be used to send messages *to* Marketplace, and can be listened on
     * for messages *from* Marketplace
     */
    const marketplaceReceiver = rawChannel.port1

    /**
     * This port can be used to send messages *to* Platform, and can be listened on
     * for messages *from* Platform
     *
     * This port is going to be transferred to the Marketplace, and will no longer
     * be usable on this end.
     */
    const platformReceiver = rawChannel.port2

    const marketplace = new MarketplaceChannel(parentWindow, config, elm, marketplaceReceiver, platformReceiver)

    const connectedMarketplace = await new Promise<Marketplace>((resolve, reject) => {
      const connectEvent = { nonce: MarketplaceChannel.generateNonce(), tag: PlatformConnectEventType.Connect }

      /**
       * Wait for the Marketplace to send the nonce back to Platform. If it does,
       * we've established a more sturdy connection since we can communicate via
       * the ports now instead of passing events via browser Window references.
       */
      marketplaceReceiver.addEventListener(
        "message",
        (event) => {
          if ("tag" in event.data && event.data.nonce === connectEvent.nonce) {
            const antiClickjack = document.getElementById("antiClickjack")

            if (antiClickjack && antiClickjack.parentNode) {
              antiClickjack.parentNode.removeChild(antiClickjack)
            }

            // Resolve this promise which means we're successfully connected
            resolve(marketplace)
          }

          reject(new Error("Unknown event: " + event.type))
        },
        {
          once: true,
        },
      )

      marketplaceReceiver.addEventListener("messageerror", reject)

      // Start receiving messages on this port
      marketplaceReceiver.start()

      // Fire off the connect message to the Marketplace parent window
      marketplace.connect(connectEvent)
    })

    marketplaceReceiver.addEventListener("message", marketplace.marketplaceMessageListener.bind(marketplace))

    return connectedMarketplace
  }

  private static generateNonce() {
    const randomByteBuffer = new Uint8Array(8)
    self.crypto.getRandomValues(randomByteBuffer)

    const randomBytes = Array.from(randomByteBuffer)
    return randomBytes.map((num) => String.fromCharCode(num)).join("")
  }

  private marketplaceMessageListener(event: MessageEvent<MarketplaceAuthEvent>) {
    switch (event.data.tag) {
      case "AuthFailed": {
        this.elm.fromWorld.send({
          tag: "ReceiveMarketplaceMessage",
          data: event.data,
        })
        break
      }
      default:
        console.log("Unhandled TAG!", event.data.tag)
    }
  }
}

export namespace AuthenticateSSO {
  /**
   * A static name given to the window opened while a user is authenticating via SSO/SAML
   *
   * When the SSO/SAML provider redirects back to the app, we can check the current window's own name
   * and see if it matches. If it does, we know the parent window is also the app.
   */
  const POPUP_WINDOW_NAME = "marketplaceAuth"

  /**
   * Open the given auth url in a pop-up window
   */
  export const openInPopup = async (
    url: string,
    { marketplaceChannel, type }: { marketplaceChannel: Marketplace | null; type: "google" | "saml" },
  ) => {
    if (!marketplaceChannel) {
      return
    }

    const marketplace: Marketplace = marketplaceChannel

    let authPopup: Window | null = null
    let authFinished: boolean = false

    /**
     * Start checking for the auth window closing
     *
     * If it closes before auth is finished, we need to let the Marketplace know
     */
    const closeListener = setInterval(() => {
      if (authPopup && authPopup.closed && !authFinished) {
        marketplace.sendEvent({
          tag: "OAuthCancelled",
        })

        clearInterval(closeListener)
      }
    }, 50)

    const cleanup = () => {
      clearInterval(closeListener)
      self.removeEventListener("message", popupWindowMessageListener)
    }

    /**
     * During the Marketplace auth flow, if the user choose to use some form of auth
     * that absolutely must leave the window, then we'll navigate there in a new window/tab/pop-up.
     *
     * When that auth is done (likely OAuth or Saml), it'll redirect back to the Platform app.
     * That means we'll have two different windows with the app open, and we want to return the
     * result of the authentication back to this window.
     *
     * Due to browser security constraints, we can't communicate to the other window because if
     * the auth isn't finished, it's likely at some other url, and the browser won't allow snooping
     * the other window's properties if it's cross-origin.
     *
     * What we can do is send a message from the other window back to this one, which is only possible
     * if the auth is actually done.
     */
    function popupWindowMessageListener(message: MessageEvent) {
      if (!message.data || typeof message.data !== "object" || !message.source) {
        return
      }

      if (!("name" in message.source) || message.source.name !== POPUP_WINDOW_NAME) {
        return
      }

      authFinished = true
      cleanup()

      /**
       * Tell the Marketplace to redirect the iframe, or report the error
       */
      if (message.data.error) {
        marketplace.sendEvent({
          tag: type === "google" ? "GoogleAuthFailed" : "SamlAuthFailed",
          reason: message.data.error,
        })
      } else {
        marketplace.sendEvent({
          tag: "RedirectIframe",
          redirectTo: message.data.authUrl,
        })
      }

      /**
       * We can close the popup now
       */
      authPopup?.close()
    }

    // Attach this listener *before* opening the pop-up
    self.addEventListener("message", popupWindowMessageListener)

    authPopup = await openPopup({ url, name: "marketplaceAuth", features: ["popup", "height=500", "width=400"] })

    // User might have pop-ups blocked. Go back to login with an error message
    if (!authPopup) {
      marketplace.sendEvent({
        tag: type === "google" ? "GoogleAuthFailed" : "SamlAuthFailed",
        reason: "PopupsBlocked",
      })

      cleanup()

      return
    }
  }

  export const handleSSORedirect = async ({ error }: { error?: string | null | undefined } = {}) => {
    const opener = self.opener as Window

    console.log("redirect to", self.location.href)

    console.log("Send auth url to iframe!")
    opener.postMessage(
      {
        authUrl: self.location.href,
        error,
      },
      {
        targetOrigin: "*",
      },
    )

    /**
     * Never resolve this promise. If we do, the Platform app will start loading. This window exists
     * solely to auth with sso/saml and then send the url back to the original window.
     */
    return new Promise(() => {})
  }
}
